import React, {useState, useEffect, useMemo} from 'react';
import {FmlxIcon, FmlxTable, FmlxTextBox} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import jsonCopy from '../lib/jsonCopy';
import './SearchUserTable.scss';

function SearchUserTable({
  height = 200,
  userData = [],
  searchFieldPlaceholder = '',
  multiple = false,
  selectedIdsChange = () => {},
  noDataLabel,
  columns,
  selectedIds,
}) {
  const [searchValue, setSearchValue] = useState('');

  const [dataSource, setDataSource] = useState([]);
  const [hiddenRowIndexes, setHiddenRowIndexes] = useState([]);
  const [sortValue, setSortValue] = useState(null);
  const [sortType, setSortType] = useState(null);

  const [selectedDataIds, setSelectedDataIds] = useState(selectedIds ?? []);

  const formatUserData = () => {
    const originalUserData = jsonCopy(userData);
    let newDataSource = [...originalUserData];

    newDataSource = newDataSource.sort((a, b) => {
      if (a[sortValue] > b[sortValue]) {
        return 1;
      }

      if (b[sortValue] > a[sortValue]) {
        return -1;
      }

      return 0;
    });

    if (sortType === FmlxTable.SortType.DESC) {
      newDataSource = newDataSource.reverse();
    }

    const newHiddenRowIndexes = [];

    if (searchValue) {
      newDataSource.forEach((e, index) => {
        if (e.name.toLowerCase().indexOf(searchValue.toLowerCase()) === -1) {
          newHiddenRowIndexes.push(index);
        }
      });
    }

    newDataSource = newDataSource.map((row) => {
      const rowCopy = jsonCopy(row);

      return rowCopy;
    });

    if (JSON.stringify(newHiddenRowIndexes) !== JSON.stringify(hiddenRowIndexes)) {
      if (!multiple) setSelectedDataIds([]);
      setHiddenRowIndexes(newHiddenRowIndexes);
    }

    setDataSource(newDataSource);
  };

  useEffect(() => {
    formatUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    formatUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, sortValue, sortType]);

  useEffect(() => {
    if (dataSource.length === 0) return;
    const selectedUserIds = [];
    selectedDataIds.forEach((id) => {
      const user = dataSource.find((data) => data?.id === id);
      selectedUserIds.push(user?.id);
    });
    selectedIdsChange(selectedUserIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataIds, dataSource]);

  const searchFieldChange = (e) => {
    setSearchValue(e.value);
  };

  const onClickRemove = () => {
    setSearchValue('');
  };

  const handleSelectedRowsChange = (selectedItems) => {
    if (dataSource.length === 0) return;
    setSelectedDataIds(selectedItems.map((item) => item?.id));
  };

  const memoizedDataSource = useMemo(() => dataSource, [dataSource]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedColumns = useMemo(() => columns, []);

  const selectedDataIndexes = useMemo(
    () =>
      memoizedDataSource.reduce(
        (indexes, ds, idx) => (selectedDataIds.includes(ds.id) ? indexes.concat(idx) : indexes),
        [],
      ),
    [selectedDataIds, memoizedDataSource],
  );

  return (
    <div className="search-user-table">
      <div className="search-field">
        <FmlxTextBox
          type="input"
          placeholder={searchFieldPlaceholder}
          value={searchValue}
          onChange={searchFieldChange}
        />
        {searchValue && (
          <span
            style={{cursor: 'pointer'}}
            className="button-remove-search"
            onClick={onClickRemove}
            onKeyDown={onClickRemove}
            role="button"
            tabIndex={0}
          >
            <FmlxIcon name="CancelOutline" variant="secondary" customColor="red" size="xs" />
          </span>
        )}
      </div>
      <div style={{height}}>
        <FmlxTable
          type={multiple ? FmlxTable.Type.MULTISELECT : FmlxTable.Type.BASIC}
          data={memoizedDataSource}
          columns={memoizedColumns}
          hiddenRowIndexes={hiddenRowIndexes}
          selectable
          multiSelect
          showCheckBoxSelection={multiple}
          getSortValue={(e) => {
            setSortValue(e.column);
            setSortType(e.type);
          }}
          selectedRowIds={selectedDataIndexes}
          placeholder={noDataLabel}
          enableDragSelection={false}
          showToolbar={false}
          getSelectedRow={handleSelectedRowsChange}
        />
      </div>
    </div>
  );
}

SearchUserTable.propTypes = {
  height: PropTypes.number,
  userData: PropTypes.array,
  searchFieldPlaceholder: PropTypes.string,
  multiple: PropTypes.bool,
  noDataLabel: PropTypes.any,
  selectedIdsChange: PropTypes.func,
  columns: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
};

SearchUserTable.defaultProps = {
  height: 200,
  userData: [],
  searchFieldPlaceholder: '',
  multiple: false,
  noDataLabel: '',
  selectedIdsChange: () => {},
};

export default SearchUserTable;
